<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch } from "vue";
import { useRoute } from "vue-router";
import { loadYouTubeApi } from "../../utils/youtube";
import { useOnboardingVideo } from "../../hooks/useOnboardingVideo";

const route = useRoute();
const { onboardingVideo, dismissVideo, savePlaybackTime, getPlaybackTime } = useOnboardingVideo();

const playerEl = ref<HTMLDivElement | null>(null);
let player: YT.Player | null = null;
let timeInterval: ReturnType<typeof setInterval> | null = null;

const isVisible = computed(
    () => onboardingVideo.isPlaying && route.path !== "/onboarding",
);

function startTracking() {
    stopTracking();
    timeInterval = setInterval(() => {
        if (player?.getPlayerState?.() === YT.PlayerState.PLAYING && onboardingVideo.videoId) {
            savePlaybackTime(onboardingVideo.videoId, player.getCurrentTime());
        }
    }, 2000);
}

function stopTracking() {
    if (timeInterval) {
        clearInterval(timeInterval);
        timeInterval = null;
    }
}

function destroyPlayer() {
    stopTracking();
    player?.destroy();
    player = null;
}

async function initPlayer() {
    destroyPlayer();

    if (!playerEl.value || !onboardingVideo.videoId) return;

    await loadYouTubeApi();

    if (!playerEl.value || !onboardingVideo.videoId) return;

    const startAt = getPlaybackTime(onboardingVideo.videoId);

    player = new YT.Player(playerEl.value, {
        videoId: onboardingVideo.videoId,
        playerVars: {
            autoplay: 1,
            rel: 0,
            start: startAt,
            modestbranding: 1,
        },
        events: {
            onReady: () => startTracking(),
            onStateChange: (event: YT.OnStateChangeEvent) => {
                if (
                    (event.data === YT.PlayerState.PAUSED ||
                        event.data === YT.PlayerState.ENDED) &&
                    onboardingVideo.videoId
                ) {
                    if (player?.getCurrentTime) {
                        savePlaybackTime(onboardingVideo.videoId, player.getCurrentTime());
                    }
                }
            },
        },
    });
}

function onClose() {
    destroyPlayer();
    dismissVideo();
}

watch(isVisible, (visible) => {
    if (visible) {
        // Wait for the DOM element to render
        setTimeout(() => initPlayer(), 0);
    } else {
        // Save time before destroying when navigating back to /onboarding
        if (player?.getPlayerState?.() === YT.PlayerState.PLAYING && onboardingVideo.videoId) {
            savePlaybackTime(onboardingVideo.videoId, player.getCurrentTime());
        }
        destroyPlayer();
    }
});

onBeforeUnmount(() => {
    destroyPlayer();
});
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isVisible"
            class="fixed bottom-4 right-4 z-[1000] w-80 rounded-lg shadow-xl overflow-hidden bg-white border border-gray-200"
        >
            <div class="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                <p class="text-sm font-medium truncate">{{ onboardingVideo.stepTitle }}</p>
                <button
                    class="flex items-center justify-center h-6 w-6 rounded hover:bg-gray-200 transition-colors"
                    @click="onClose"
                >
                    <span class="material-symbols-outlined text-base">close</span>
                </button>
            </div>
            <div class="aspect-video bg-black">
                <div ref="playerEl" class="w-full h-full" />
            </div>
        </div>
    </Teleport>
</template>
